import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { AngularFireFunctions } from '@angular/fire/functions';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, first } from 'rxjs/operators';
import { AuthService } from '../../services/auth.service';
import { User } from '../../models/user.model';

const dismissStorageKey = (uid: string) =>
  `vaultwrx-bank-charge-consent-dismissed-${uid}`;

export interface BankChargeConsentStatus {
  userId: string;
  userType: 'retailer' | null;
  profileId: string | null;
  profilePath: string | null;
  hasStripeCustomerId: boolean;
  hasMandate: boolean;
  requiresConsent: boolean;
}

@Component({
  selector: 'app-bank-charge-consent-banner',
  templateUrl: './bank-charge-consent-banner.component.html',
  styleUrls: ['./bank-charge-consent-banner.component.scss'],
})
export class BankChargeConsentBannerComponent implements OnInit, OnDestroy {
  checked = false;
  accepting = false;
  loading = true;
  requiresConsent = false;
  private subscription?: Subscription;

  constructor(
    public auth: AuthService,
    private fns: AngularFireFunctions,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.subscription = this.auth.user$
      .pipe(distinctUntilChanged((a, b) => a?.uid === b?.uid))
      .subscribe((user) => {
        if (!user || !this.auth.isRetailer(user)) {
          this.requiresConsent = false;
          this.loading = false;
          this.cdr.markForCheck();
          return;
        }
        this.refreshConsentStatus();
      });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  showConsentBanner(user: User | null | undefined): boolean {
    if (!user || this.loading || !this.auth.isRetailer(user)) {
      return false;
    }
    return this.requiresConsent && !this.isDismissedForSession(user.uid);
  }

  dismissBanner(user: User): void {
    if (user?.uid) {
      sessionStorage.setItem(dismissStorageKey(user.uid), 'true');
    }
    this.requiresConsent = false;
    this.checked = false;
    this.cdr.markForCheck();
  }

  private isDismissedForSession(uid: string): boolean {
    return sessionStorage.getItem(dismissStorageKey(uid)) === 'true';
  }

  async acceptConsent(): Promise<void> {
    if (!this.checked || this.accepting) {
      return;
    }

    const ip_address = await this.getClientIpAddress();
    const user_agent = navigator.userAgent || '';

    if (!ip_address || !user_agent) {
      console.error('Unable to collect ip_address or user_agent for mandate.');
      return;
    }

    this.accepting = true;
    this.cdr.markForCheck();

    try {
      await this.fns
        .httpsCallable('recordBankChargeMandate')({ ip_address, user_agent })
        .pipe(first())
        .toPromise();
      this.requiresConsent = false;
      this.checked = false;
    } catch (error) {
      console.error('Error recording bank charge mandate', error);
    } finally {
      this.accepting = false;
      this.cdr.markForCheck();
    }
  }

  private refreshConsentStatus(): void {
    this.loading = true;
    this.cdr.markForCheck();

    this.fns
      .httpsCallable('checkBankChargeMandate')({})
      .pipe(first())
      .subscribe(
        (status: BankChargeConsentStatus) => {
          if (status?.profilePath) {
            console.info(
              '[bank-charge-consent] checked',
              status.profilePath,
              { hasMandate: status.hasMandate, requiresConsent: status.requiresConsent }
            );
          }
          this.requiresConsent =
            !!status?.requiresConsent &&
            !(status?.userId && this.isDismissedForSession(status.userId));
          this.loading = false;
          this.cdr.markForCheck();
        },
        (error) => {
          console.error('Error checking bank charge mandate', error);
          this.requiresConsent = false;
          this.loading = false;
          this.cdr.markForCheck();
        }
      );
  }

  private async getClientIpAddress(): Promise<string> {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const body = await response.json();
      return typeof body?.ip === 'string' ? body.ip : '';
    } catch {
      return '';
    }
  }
}
